import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetShowTransports, apiCreateShowTransport, apiUpdateShowTransport, apiDeleteShowTransport,
  apiGetFlightLookup,
  apiSearchAirports, apiSearchAirlines,
  type MemberFlightDTO, type AirportDTO,
} from '../api';
import FlightStatusBadge from './FlightStatusBadge';
import ImportFlightsAIModal from './ImportFlightsAIModal';
import AutoTextarea from './AutoTextarea';
import { reportSaveError } from '../lib/saveStatus';
import { createWriteQueue } from '../lib/writeQueue';
import { computeFlightDuration, computeTrainDuration } from '../lib/flightDuration';
import { apiGetAirportTimezones } from '../api';
import { useWriteGate } from '../lib/writeGate';

// Unified transport legs (flight | train | ferry) for a show day. One row = one
// leg / booking; the members on it are chosen with checkboxes (multi-person).
// Flight mode keeps the airport + airline autocomplete; train mode offers a
// station + operator datalist (with class / seat / platform).
interface Props {
  realId: string | null;
  /** Membres proposés sur un leg : `{ id, name, defaultAirport }` (aéroport de
      préférence de la fiche, sinon celui du groupe — pré-remplissage du From). */
  members?: Array<{ id: string | number; name: string; defaultAirport?: string }>;
  notes?: string;
  onNotesChange?: (v: string) => void;
  /** The show's band — feeds the capability-aware write gate (lot A1). */
  bandId?: string | null;
  /**
   * Reports whether this section holds content (at least one transport leg,
   * LOT E1): the hosting card opens itself when it does. No extra fetch — the
   * section already loads its own legs.
   */
  onHasDataChange?: (hasData: boolean) => void;
}

interface TransportView extends MemberFlightDTO {
  memberIdSet: Set<string>;
}

const MODES: Array<{ value: string; label: string }> = [
  { value: 'flight', label: '✈️ Flight' },
  { value: 'train', label: '🚆 Train' },
  { value: 'ferry', label: '⛴️ Ferry' },
];

// Small station / operator suggestion lists (auto-complete feel without a catalog).
const STATIONS = [
  'Genève', 'Lausanne', 'Zürich HB', 'Bern', 'Bâle CFF', 'Lyon Part-Dieu', 'Paris Gare de Lyon',
  'Paris Nord', 'Berlin Hbf', 'Hamburg Hbf', 'Amsterdam Centraal', 'Brussels Midi', 'Rotterdam Centraal',
  'Frankfurt Hbf', 'Milan Centrale', 'Vienna Hbf', 'Munich Hbf', 'London St Pancras', 'Madrid Atocha',
  'Barcelona Sants',
];
const OPERATORS = ['SBB', 'TGV Lyria', 'Eurostar', 'SNCF', 'Deutsche Bahn', 'ÖBB', 'Trenitalia', 'Renfe'];

function toView(f: MemberFlightDTO): TransportView {
  let ids: string[] = [];
  try { ids = JSON.parse(f.member_ids || '[]'); } catch { ids = []; }
  if (ids.length === 0 && f.team_member_id) ids = [String(f.team_member_id)];
  return { ...f, memberIdSet: new Set(ids.map(String)) };
}

export default function TransportSection({ realId, members = [], notes = '', onNotesChange, bandId, onHasDataChange }: Props) {
  const { t } = useTranslation();
  // Capability-aware write gate (lot A1): a global read-only account (even with a
  // band editor role) must not emit transport writes — the server 403s them.
  const gate = useWriteGate(bandId);
  const canWrite = gate.canWrite;
  const [legs, setLegs] = useState<TransportView[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Report "this section has content" upward (LOT E1) without re-subscribing
  // the effect to a callback identity: the ref always holds the latest one.
  const onHasDataRef = useRef(onHasDataChange);
  onHasDataRef.current = onHasDataChange;
  const hasData = legs.length > 0;
  useEffect(() => { onHasDataRef.current?.(hasData); }, [hasData]);
  const [fromResults, setFromResults] = useState<AirportDTO[]>([]);
  const [toResults, setToResults] = useState<AirportDTO[]>([]);
  const [airlineResults, setAirlineResults] = useState<{ iata?: string; name: string }[]>([]);
  const [otherOpen, setOtherOpen] = useState<Set<string>>(new Set());
  // Auto-fill "Look up" per leg: busy set + transient message.
  const [lookupBusy, setLookupBusy] = useState<Set<string>>(new Set());
  const [lookupMsg, setLookupMsg] = useState<Record<string, string>>({});
  // LOT A2 — "Import from booking (AI)": the modal is only mounted when opened
  // (and only reachable through the gated button below).
  const [aiImportOpen, setAiImportOpen] = useState(false);

  const load = useCallback(async () => {
    if (!realId) return;
    const t = await apiGetShowTransports(realId).catch(() => []);
    const sorted = [...t].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map(toView);
    setLegs(sorted);
    const open = new Set<string>();
    for (const lv of sorted) if ((lv.person_name || '').trim()) open.add(String(lv.id));
    setOtherOpen(open);
    setLoaded(true);
  }, [realId]);

  useEffect(() => { void load(); }, [load]);

  const searchAirport = async (q: string, setter: (r: AirportDTO[]) => void): Promise<AirportDTO[]> => {
    if (!q || q.trim().length < 2) { setter([]); return []; }
    try { const r = await apiSearchAirports(q.trim()); setter(r); return r; } catch { setter([]); return []; }
  };
  const searchAirline = async (q: string) => {
    if (!q || q.trim().length < 2) { setAirlineResults([]); return; }
    try { setAirlineResults(await apiSearchAirlines(q.trim())); } catch { setAirlineResults([]); }
  };

  // Flight mode From/To resolve airport IATA + city via autocomplete.
  const onAirport = async (fv: TransportView, which: 'from' | 'to', value: string) => {
    const setter = which === 'from' ? setFromResults : setToResults;
    const results = await searchAirport(value, setter);
    const up = value.trim().toUpperCase();
    const hit = results.find((a) => a.iata === up) || results.find((a) => a.city.toUpperCase() === up);
    if (which === 'from') {
      updateLeg(fv, hit ? { from_iata: hit.iata, from_city: hit.city } : { from_iata: '', from_city: value });
    } else {
      updateLeg(fv, hit ? { to_iata: hit.iata, to_city: hit.city } : { to_iata: '', to_city: value });
    }
  };

  // AUTO FLIGHT DURATION (2026-10-18, user): when the local departure/arrival
  // times AND both airport IATAs are known, fill the Duration from the
  // wall-clock gap corrected by the airports' UTC offsets (airports.tz —
  // DST-aware via Intl). The field stays editable: the next change recomputes.
  const tzCache = useRef<Map<string, string>>(new Map());
  const [tzVersion, setTzVersion] = useState(0);
  useEffect(() => {
    for (const fv of legs) {
      if (fv.mode !== 'flight' && fv.mode !== 'train') continue;
      const dep = (fv.departure || '').trim();
      const arr = (fv.arrival || '').trim();
      // TRAIN (SPEC-TRAIN-LEG-FIXES 2026-10-20): same auto-duration pattern as
      // flights but WITHOUT the timezone correction — station times are local
      // and trains stay in one timezone; a negative gap (crosses midnight,
      // e.g. 23:10 → 00:40) rolls over +24 h → 01:30. The field stays editable
      // like for flights (the next dep/arr change recomputes).
      if (fv.mode === 'train') {
        if (!dep || !arr) continue;
        const d = computeTrainDuration(dep, arr);
        if (d && d !== (fv.duration || '')) void updateLeg(fv, { duration: d });
        continue;
      }
      const fi = (fv.from_iata || '').trim().toUpperCase();
      const ti = (fv.to_iata || '').trim().toUpperCase();
      if (!dep || !arr || fi.length !== 3 || ti.length !== 3) continue;
      const missing = [fi, ti].filter((x) => !tzCache.current.has(x));
      if (missing.length) {
        apiGetAirportTimezones(missing)
          .then((m) => {
            for (const [k, v] of Object.entries(m)) tzCache.current.set(k, v);
            for (const x of missing) if (!tzCache.current.has(x)) tzCache.current.set(x, '');
            setTzVersion((v) => v + 1);
          })
          .catch(() => {
            for (const x of missing) tzCache.current.set(x, '');
          });
        continue;
      }
      const date = (fv.flight_date || '').trim() || new Date().toISOString().slice(0, 10);
      const d = computeFlightDuration(dep, arr, tzCache.current.get(fi) || '', tzCache.current.get(ti) || '', date);
      if (d && d !== (fv.duration || '')) void updateLeg(fv, { duration: d });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legs, tzVersion, realId, canWrite]);

  const addLeg = async (mode = 'flight') => {
    if (!realId || !canWrite) return;
    try {
      const f = await apiCreateShowTransport(realId, {
        mode, person_name: '', member_ids: '[]', from_city: '', to_city: '', from_station: '', to_station: '',
        departure: '', arrival: '', airline: '', flight_no: '', train_class: '', coach: '', seat: '', platform: '',
        booking_ref: '', notes: '', duration: '', sort_order: legs.length,
      });
      setLegs((p) => [...p, toView(f)]);
    } catch (e) { reportSaveError('show', e); }
  };

  // Optimistic + debounced per leg (LOT TYPING-AND-SETLIST-LAYOUT, 2026-11-04):
  // every character in a leg field used to fire a PUT and then re-write the row
  // from the ~80 ms-old reply (letters typed in between were erased).
  const legsQueue = useRef(createWriteQueue()).current;
  const legsPending = useRef<Map<string, MemberFlightDTO>>(new Map());
  useEffect(() => () => { legsQueue.flushAll(); }, [legsQueue]);

  const updateLeg = (fv: TransportView, patch: Partial<MemberFlightDTO>) => {
    if (!realId || !canWrite) return;
    // Local state first, including the member set (a checkbox toggle must show
    // immediately, without waiting for the round-trip).
    setLegs((p) => p.map((x) => {
      if (x.id !== fv.id) return x;
      if (patch.member_ids === undefined) return { ...x, ...patch };
      let ids: string[] = [];
      try { ids = JSON.parse(patch.member_ids || '[]'); } catch { ids = []; }
      return { ...x, ...patch, memberIdSet: new Set(ids.map(String)) };
    }));
    // Strip the TransportView-only `memberIdSet` (a Set) out of the DTO payload
    // before sending — it is not a MemberFlightDTO field.
    const { memberIdSet: _memberIdSet, ...fvRest } = { ...fv, ...patch };
    const key = String(fv.id);
    legsPending.current.set(key, fvRest);
    legsQueue.schedule(key, async () => {
      const payload = legsPending.current.get(key);
      if (!payload) return;
      legsPending.current.delete(key);
      try {
        await apiUpdateShowTransport(realId, fv.id, payload);
      } catch (e: any) {
        console.error('[Transports] save failed:', e);
        reportSaveError('show', e);
      }
    });
  };
  const toggleMember = async (fv: TransportView, mid: string | number) => {
    const next = new Set(fv.memberIdSet);
    const s = String(mid);
    const adding = !next.has(s);
    if (adding) next.add(s); else next.delete(s);
    // AÉROPORT DE PRÉFÉRENCE (2026-10-17) : en ajoutant un membre sur un leg VOL
    // dont le From est vide, on pré-remplit l'origine depuis la fiche du membre
    // (default_airport) — chaque personne peut venir d'endroits différents.
    if (adding && fv.mode === 'flight' && !fv.from_iata && !fv.from_city) {
      const mem = members.find((m) => String(m.id) === s);
      if (mem?.defaultAirport) { updateLeg(fv, { member_ids: JSON.stringify([...next]), from_iata: mem.defaultAirport }); return; }
    }
    updateLeg(fv, { member_ids: JSON.stringify([...next]) });
  };
  const removeLeg = async (fv: TransportView) => {
    if (!realId || !canWrite) return;
    legsQueue.cancel(String(fv.id));
    legsPending.current.delete(String(fv.id));
    try {
      await apiDeleteShowTransport(realId, fv.id);
      setLegs((p) => p.filter((x) => x.id !== fv.id));
    } catch (e) { reportSaveError('show', e); }
  };

  // Extract HH:MM from a provider datetime string ("14:30", "14:30:00",
  // "2026-09-13T14:30:00Z", …) so it fits the member_flights time fields.
  const timeOnly = (v?: string): string | undefined => {
    if (!v) return undefined;
    const m = String(v).match(/(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : v;
  };

  // Auto-fill lookup: pre-fill airline / route (IATA + cities) / scheduled times
  // from the flight provider. available:false → discreet note, no crash.
  const doLookup = async (fv: TransportView) => {
    const id = String(fv.id);
    const flightNo = (fv.flight_no || '').trim();
    const date = (fv.flight_date || '').trim();
    if (!flightNo) {
      setLookupMsg((p) => ({ ...p, [id]: t('flights.needsFlightNo') }));
      return;
    }
    if (!date) {
      setLookupMsg((p) => ({ ...p, [id]: t('flights.needsDate') }));
      return;
    }
    setLookupBusy((p) => new Set(p).add(id));
    setLookupMsg((p) => ({ ...p, [id]: '' }));
    try {
      const res = await apiGetFlightLookup(flightNo, date);
      if (!res || res.available === false) {
        setLookupMsg((p) => ({ ...p, [id]: t('flights.lookupUnavailable') }));
        return;
      }
      const patch: Partial<MemberFlightDTO> = {};
      if (res.airline) patch.airline = res.airline;
      if (res.from_iata) patch.from_iata = res.from_iata;
      if (res.to_iata) patch.to_iata = res.to_iata;
      if (res.from_city) patch.from_city = res.from_city;
      if (res.to_city) patch.to_city = res.to_city;
      const dep = timeOnly(res.scheduled_dep);
      const arr = timeOnly(res.scheduled_arr);
      if (dep) patch.departure = dep;
      if (arr) patch.arrival = arr;
      await updateLeg(fv, patch);
    } catch {
      setLookupMsg((p) => ({ ...p, [id]: t('flights.lookupFailed') }));
    } finally {
      setLookupBusy((p) => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  const toggleOther = (fv: TransportView) => {
    const s = String(fv.id);
    const opening = !otherOpen.has(s);
    const next = new Set(otherOpen);
    if (opening) next.add(s); else next.delete(s);
    setOtherOpen(next);
    if (!opening) updateLeg(fv, { person_name: '' });
  };

  const mode = (fv: TransportView) => (fv.mode || 'flight');

  return (
    <div data-daytab="transport">
      {/* Add-transport buttons LEFT-ALIGNED ABOVE the NOTES field
          (user 2026-10-24: they were tucked in the header actions). */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 10px' }} data-transport-add>
        {MODES.map((m) => (
          <button key={m.value} className="btn btn-small" onClick={() => addLeg(m.value)} disabled={!canWrite}>+ {m.label}</button>
        ))}
        {/* LOT A2 — AI import. Hidden (not just disabled) for a read-only account:
            the apply route requires canWriteBand, so the button must not exist. */}
        {canWrite ? (
          <button
            className="btn btn-small"
            onClick={() => setAiImportOpen(true)}
            data-transport-ai-import
          >
            {t('transport.aiImport')}
          </button>
        ) : null}
      </div>
      <div className="rc-card-head">
        <h4 className="rc-header-title">{t('transport.title')}</h4>
      </div>
      {loaded && legs.length === 0 ? (
        <p style={{ color: 'var(--text-dim)' }}>{t('transport.noLegs')}</p>
      ) : null}
      <div className="rc-list" data-transport-list>
        {legs.map((fv) => {
          const m = mode(fv);
          return (
          <div className="rc-card col" data-transport-leg key={fv.id}>
            {/* Mode + members on this leg */}
            <div className="rc-card-fields">
              <select className="form-input" style={{ flex: '0 0 auto', width: 140, padding: '2px 6px', fontSize: '0.82rem' }}
                value={m}
                disabled={!canWrite}
                onChange={(e) => updateLeg(fv, { mode: e.target.value })}>
                {MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', width: 58 }}>{t('transport.people')}</span>
              {members.length === 0 ? (
                <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)' }}>{t('transport.noMembers')}</span>
              ) : members.map((mem) => (
                <label key={String(mem.id)} style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 'var(--font-sm)' }}>
                  <input type="checkbox" checked={fv.memberIdSet.has(String(mem.id))} disabled={!canWrite} onChange={() => toggleMember(fv, mem.id)} />
                  {mem.name}
                </label>
              ))}
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 'var(--font-sm)' }}>
                <input type="checkbox" checked={otherOpen.has(String(fv.id))} disabled={!canWrite} onChange={() => toggleOther(fv)} />
                {t('transport.other')}
              </label>
              {fv.memberIdSet.size > 0 && (
                <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>
                  {t('transport.onThisLeg', { count: fv.memberIdSet.size })}
                </span>
              )}
            </div>

            {otherOpen.has(String(fv.id)) && (
              <div className="rc-card-fields">
                <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', width: 70 }}>{t('transport.otherLabel')}</span>
                <input className="form-input" style={{ flex: '1 1 220px' }}
                  value={fv.person_name || ''}
                  placeholder={t('transport.manualNames')}
                  disabled={!canWrite}
                  onChange={(e) => updateLeg(fv, { person_name: e.target.value })}
                />
              </div>
            )}

            {/* Route line 1 (identity), full width */}
            <div className="rc-card-fields">
              <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', width: 70 }}>{m === 'train' ? t('transport.train') : m === 'ferry' ? t('transport.ferry') : t('transport.flight')}</span>
              <input className="form-input" style={{ flex: '1 1 80px', minWidth: 70 }} value={fv.booking_ref || ''} disabled={!canWrite} onChange={(e) => updateLeg(fv, { booking_ref: e.target.value })} placeholder={t('transport.ref')} />
              <input className="form-input" style={{ flex: '2 1 160px', minWidth: 120 }}
                list={m === 'flight' ? 'transport-airlines' : 'transport-operators'}
                value={fv.airline || ''}
                disabled={!canWrite}
                onChange={(e) => { updateLeg(fv, { airline: e.target.value }); if (m === 'flight') searchAirline(e.target.value); }}
                placeholder={m === 'flight' ? t('transport.airline') : m === 'train' ? t('transport.operator') : t('transport.company')}
              />
              <input className="form-input" style={{ flex: '1 1 90px', minWidth: 70 }} value={fv.flight_no || ''}
                disabled={!canWrite}
                onChange={(e) => updateLeg(fv, { flight_no: e.target.value })}
                placeholder={m === 'flight' ? t('transport.flightNo') : m === 'train' ? t('transport.trainNo') : t('transport.route')} />
              {m === 'flight' && (
                <>
                  <input
                    className="form-input"
                    type="date"
                    style={{ flex: '0 0 auto', width: 140 }}
                    value={fv.flight_date || ''}
                    aria-label={t('flights.date')}
                    disabled={!canWrite}
                    onChange={(e) => updateLeg(fv, { flight_date: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-small"
                    style={{ fontSize: 'var(--font-xs)', padding: '1px 8px', flex: '0 0 auto' }}
                    disabled={lookupBusy.has(String(fv.id)) || !canWrite}
                    onClick={() => void doLookup(fv)}
                  >
                    {lookupBusy.has(String(fv.id)) ? t('flights.fetching') : t('flights.lookUp')}
                  </button>
                  <FlightStatusBadge
                    key={`${String(fv.id)}-${fv.flight_no || ''}-${fv.flight_date || ''}`}
                    flightNo={fv.flight_no || ''}
                    date={fv.flight_date || ''}
                    autoLoad
                  />
                  {lookupMsg[String(fv.id)] && (
                    <span className="text-dim" style={{ fontSize: 'var(--font-xs)', flex: '0 0 auto' }}>
                      {lookupMsg[String(fv.id)]}
                    </span>
                  )}
                </>
              )}
              {m === 'flight' ? (
                <>
                  <input className="form-input" style={{ flex: '1 1 90px', minWidth: 70 }}
                    list="transport-from-airports"
                    value={fv.from_iata || fv.from_city || ''}
                    placeholder={t('transport.from')}
                    disabled={!canWrite}
                    onChange={(e) => onAirport(fv, 'from', e.target.value)}
                  />
                  <span className="text-dim">→</span>
                  <input className="form-input" style={{ flex: '1 1 90px', minWidth: 70 }}
                    list="transport-to-airports"
                    value={fv.to_iata || fv.to_city || ''}
                    placeholder={t('transport.to')}
                    disabled={!canWrite}
                    onChange={(e) => onAirport(fv, 'to', e.target.value)}
                  />
                </>
              ) : (
                <>
                  <input className="form-input" style={{ flex: '1 1 90px', minWidth: 70 }}
                    list="transport-stations"
                    value={fv.from_station || fv.from_city || ''}
                    placeholder={t('transport.from')}
                    disabled={!canWrite}
                    onChange={(e) => updateLeg(fv, { from_station: e.target.value, from_city: '' })}
                  />
                  <span className="text-dim">→</span>
                  <input className="form-input" style={{ flex: '1 1 90px', minWidth: 70 }}
                    list="transport-stations"
                    value={fv.to_station || fv.to_city || ''}
                    placeholder={t('transport.to')}
                    disabled={!canWrite}
                    onChange={(e) => updateLeg(fv, { to_station: e.target.value, to_city: '' })}
                  />
                </>
              )}
            </div>

            {/* Route line 2 (times + mode-specific extras) */}
            <div className="rc-card-fields">
              <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', width: 70 }}>{t('transport.times')}</span>
              <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>
                {t('transport.dep')}
                <input className="form-input" style={{ width: 110 }} type="time" value={fv.departure || ''} disabled={!canWrite} onChange={(e) => updateLeg(fv, { departure: e.target.value })} />
              </label>
              <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>
                {t('transport.arr')}
                <input className="form-input" style={{ width: 110 }} type="time" value={fv.arrival || ''} disabled={!canWrite} onChange={(e) => updateLeg(fv, { arrival: e.target.value })} />
              </label>
              <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>
                {t('transport.duration')}
                <input className="form-input" style={{ width: 110 }} type="time" value={fv.duration || ''} disabled={!canWrite} onChange={(e) => updateLeg(fv, { duration: e.target.value })} />
              </label>
              {m === 'train' && (
                <>
                  <input className="form-input" style={{ width: 100 }} value={fv.train_class || ''}
                    list="transport-classes" placeholder={t('transport.class')}
                    disabled={!canWrite}
                    onChange={(e) => updateLeg(fv, { train_class: e.target.value })} />
                  <input className="form-input" style={{ width: 100 }} value={fv.coach || ''}
                    placeholder={t('transport.coach')}
                    disabled={!canWrite} onChange={(e) => updateLeg(fv, { coach: e.target.value })} />
                  <input className="form-input" style={{ width: 90 }} value={fv.seat || ''}
                    placeholder={t('transport.seat')} disabled={!canWrite} onChange={(e) => updateLeg(fv, { seat: e.target.value })} />
                  <input className="form-input" style={{ width: 90 }} value={fv.platform || ''}
                    placeholder={t('transport.platform')} disabled={!canWrite} onChange={(e) => updateLeg(fv, { platform: e.target.value })} />
                </>
              )}
              <button className="btn btn-secondary" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={() => removeLeg(fv)} disabled={!canWrite}>×</button>
            </div>
          </div>
          );
        })}
      </div>
      <datalist id="transport-from-airports">
        {fromResults.map((a) => <option key={a.iata} value={a.iata}>{a.city} — {a.country}</option>)}
      </datalist>
      <datalist id="transport-to-airports">
        {toResults.map((a) => <option key={a.iata} value={a.iata}>{a.city} — {a.country}</option>)}
      </datalist>
      <datalist id="transport-airlines">
        {airlineResults.map((a, i) => <option key={a.iata || `${a.name}-${i}`} value={a.name}>{a.iata || ''}</option>)}
      </datalist>
      <datalist id="transport-operators">
        {OPERATORS.map((o) => <option key={o} value={o} />)}
      </datalist>
      <datalist id="transport-stations">
        {STATIONS.map((s) => <option key={s} value={s} />)}
      </datalist>
      <datalist id="transport-classes">
        <option value="1st" /><option value="2nd" /><option value="1st class" /><option value="2nd class" />
      </datalist>
      <div className="form-field rc-form-row col" style={{ marginTop: 12 }} data-transport-notes>
        <label>{t('transport.notes')}</label>
        <AutoTextarea
          className="form-input"
          rows={2}
          value={notes}
          placeholder={t('transport.notesPh')}
          disabled={!canWrite}
          onChange={(e) => onNotesChange?.(e.target.value)}
        />
      </div>
      {aiImportOpen && realId && canWrite ? (
        <ImportFlightsAIModal
          bandId={bandId ?? null}
          showId={realId}
          members={members.map((m) => ({ id: m.id, name: m.name }))}
          canWrite={canWrite}
          onClose={() => setAiImportOpen(false)}
          // Reload AFTER the apply answer (never during a queued mutation).
          onImported={() => void load()}
        />
      ) : null}
    </div>
  );
}
